import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { LuExternalLink } from 'react-icons/lu';
import { INPUT_CLS } from 'helpers/formStyles';
import { formatDate } from 'helpers/utils';

const TH_CLS = 'tw-px-4 tw-py-2 tw-text-left tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-500';
const TD_CLS = 'tw-px-4 tw-py-2 tw-text-sm tw-align-middle';

const humanize = (status) => (status ? status.replace(/_/g, ' ') : '—');

// Step 1: for every building of the contract, the CO# whose PIF is the reference.
// Saved on change; until then the latest non-rejected CO# is pre-selected.
const PifSourcePicker = ({ projectId, buildings, canEdit, locked, onChange }) => {
  const [savingId, setSavingId] = useState(null);

  const change = async (building, adaptationId) => {
    setSavingId(building.building_id);
    try {
      await onChange(building, adaptationId);
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="tw-overflow-x-auto">
      <table className="tw-w-full tw-border-collapse">
        <thead>
          <tr className="tw-border-0 tw-border-b tw-border-solid tw-border-slate-200">
            <th className={TH_CLS}>Building</th>
            <th className={TH_CLS}>PIF of CO#</th>
            <th className={TH_CLS}>CO# status</th>
            <th className={TH_CLS}>CO# date</th>
            <th className={TH_CLS}><span className="tw-sr-only">Open PIF</span></th>
          </tr>
        </thead>
        <tbody>
          {buildings.map((building) => {
            const selected = building.change_orders.find((co) => co.adaptation_id === building.selected_adaptation_id);
            return (
              <tr key={building.building_id} className="tw-border-0 tw-border-b tw-border-solid tw-border-slate-100 last:tw-border-b-0">
                <td className={`${TD_CLS} tw-font-semibold tw-text-slate-800`}>{building.building_name}</td>
                <td className={TD_CLS}>
                  {building.change_orders.length === 0 ? (
                    <span className="tw-text-xs tw-text-amber-700">No CO# with a PIF yet</span>
                  ) : (
                    <div className="tw-flex tw-items-center tw-gap-2">
                      <select
                        className={`${INPUT_CLS} !tw-w-auto !tw-py-1`}
                        aria-label={`PIF of ${building.building_name}`}
                        value={building.selected_adaptation_id || ''}
                        onChange={(e) => change(building, Number(e.target.value))}
                        disabled={!canEdit || locked || savingId !== null}
                      >
                        {building.change_orders.map((co) => (
                          <option key={co.adaptation_id} value={co.adaptation_id}>{co.label}</option>
                        ))}
                      </select>
                      {!building.selection_saved && (
                        <span className="tw-text-[11px] tw-text-slate-400" title="Not chosen yet — the latest CO# that was not rejected is pre-selected">
                          suggested
                        </span>
                      )}
                    </div>
                  )}
                </td>
                <td className={`${TD_CLS} tw-text-xs tw-text-slate-600 tw-capitalize`}>{humanize(selected?.co_status)}</td>
                <td className={`${TD_CLS} tw-text-xs tw-text-slate-600`}>{selected?.entry_date ? formatDate(selected.entry_date) : '—'}</td>
                <td className={`${TD_CLS} tw-text-right`}>
                  {selected && (
                    <Link
                      to={`/crm/projects/${projectId}/buildings/${building.building_id}/adaptations/${selected.adaptation_id}/pifs/${selected.pif_id}`}
                      target="_blank" rel="noopener noreferrer"
                      className="tw-inline-flex tw-items-center tw-gap-1 tw-text-xs tw-text-primary-600 tw-whitespace-nowrap"
                    >
                      View PIF <LuExternalLink size={11} />
                    </Link>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

PifSourcePicker.propTypes = {
  projectId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  buildings: PropTypes.array.isRequired,
  canEdit:   PropTypes.bool,
  locked:    PropTypes.bool,
  onChange:  PropTypes.func.isRequired,
};

export default PifSourcePicker;
